/**
 * Signin Integration Helper
 *
 * Add this to your signin flow to automatically save upload token and UUID
 * from the signin response.
 */

import { processSigninResponse } from './authHelpers';

/**
 * Process signin response and save all tokens
 *
 * Call this after successful signin to extract:
 * - data.token → User token (localStorage: 'token')
 * - api_response.access → Upload token (localStorage: 'uploader_token')
 * - api_response.uuid → User UUID (localStorage: 'user_uuid')
 *
 * @param {Object} response - Full signin API response
 * @returns {Object} Summary of what was saved
 *
 * @example
 * // In your signin handler:
 * const response = await axios.post('/v4/signin', { email, password });
 * if (response.data.success) {
 *   handleSigninResponse(response.data);
 *   // Continue with normal flow...
 * }
 */
export function handleSigninResponse(response) {
  console.log('[SigninIntegration] Processing signin response...');

  // Save user token (existing behavior - keep for backward compatibility)
  if (response.data?.token) {
    localStorage.setItem('token', response.data.token);
    console.log('[SigninIntegration] User token saved');
  }

  // Save upload token and UUID using our helper
  if (response.api_response) {
    processSigninResponse(response);
    console.log('[SigninIntegration] Upload token and UUID saved');
  } else {
    console.warn('[SigninIntegration] No api_response in signin response!');
  }

  // Summary
  return {
    hasUserToken: !!response.data?.token,
    hasUploadToken: !!response.api_response?.access,
    hasUuid: !!response.api_response?.uuid,
    uploadTokenIsJWT: response.api_response?.access?.split('.').length === 3,
  };
}

/**
 * TEMPORARY FIX: Manually save tokens from your signin response
 *
 * Run this in browser console AFTER signin if you don't want to modify signin code yet:
 *
 * @example
 * // Copy your signin response, then run:
 * window.saveTokensFromResponse({
 *   data: { token: "..." },
 *   api_response: {
 *     access: "eyJhbGc...",
 *     uuid: "e6694943-..."
 *   }
 * });
 */
export function saveTokensFromResponse(response) {
  console.log('[SigninIntegration] Manually saving tokens...');

  const result = handleSigninResponse(response);

  console.log('\n✅ Tokens saved!');
  console.log('User token:', !!result.hasUserToken);
  console.log('Upload token (JWT):', result.hasUploadToken && result.uploadTokenIsJWT);
  console.log('User UUID:', result.hasUuid);
  console.log('\nRun window.debugToken() to verify!');

  return result;
}

// Make available globally for console testing
if (typeof window !== 'undefined') {
  window.saveTokensFromResponse = saveTokensFromResponse;
  console.log('💡 Helper loaded! Use window.saveTokensFromResponse(response) to manually save tokens.');
}

export default { handleSigninResponse, saveTokensFromResponse };
